import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import api, { money2, timeAgo } from "@/lib/api";
import { PageHeader, SectionTitle, Thumb, Pill } from "@/components/Bits";

const STATUS_TONE = {
  "Needs Review": "bg-[#F6DED6] text-[#A8492E]",
  "Alternative Requested": "bg-[#EDEFC4] text-ink",
  Approved: "bg-[#BEC221] text-ink",
  Responded: "bg-[#DDE3C8] text-ink",
  Resolved: "bg-line text-stone",
  Superseded: "bg-line text-fog",
};

const CLIENT_STATUS_COPY = {
  "Needs Review": "Awaiting Your Approval",
  "Alternative Requested": "With Your Designer",
  Approved: "Approved",
  Responded: "Answered",
  Resolved: "Resolved",
  Superseded: "No Longer Needed",
};

const CHOICE_STATUS_COPY = { "Needs Review": "Your Choice Needed", Responded: "You Chose" };

const ACTION_COPY = {
  selection_approval: "Review Selection",
  alternative_review: "View Request",
  presentation_approval: "Review Presentation",
  question_response: "Answer Question",
  option_choice: "Compare Options",
};

function DecisionRow({ d }) {
  return (
    <Link
      to={d.link || "/"}
      data-testid={`decision-${d.id}`}
      className="group flex flex-col gap-4 border-b border-line px-5 py-5 transition-colors last:border-0 hover:bg-bone sm:flex-row sm:items-center sm:gap-5 sm:px-6"
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <Thumb src={d.image} alt={d.title} className="h-14 w-16 shrink-0" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-serif text-lg leading-snug text-ink">{d.title}</span>
            <Pill tone="bg-line text-stone">{d.type_label}</Pill>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fog">
            {d.price ? <span className="num text-stone">{money2(d.price)}</span> : null}
            {d.count ? <span>{d.count} selections</span> : null}
            {d.context ? <span className="max-w-xs truncate">“{d.context}”</span> : null}
            <span className="num">{timeAgo(d.resolved_at || d.created_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-4">
        <Pill testId={`decision-status-${d.id}`} tone={STATUS_TONE[d.status] || "bg-line text-stone"}>
          {(d.type === "option_choice" && CHOICE_STATUS_COPY[d.status]) || CLIENT_STATUS_COPY[d.status] || d.status}
        </Pill>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-lime-dark">
          {ACTION_COPY[d.type] || "Review"}
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
        </span>
      </div>
    </Link>
  );
}

export default function Decisions() {
  const [scope, setScope] = useState("open");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get("/decisions", { params: { scope } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [scope]);

  const grouped = useMemo(() => {
    const out = [];
    (rows || []).forEach((d) => {
      let project = out.find((p) => p.project_id === d.project_id);
      if (!project) {
        project = { project_id: d.project_id, project_name: d.project_name, spaces: [] };
        out.push(project);
      }
      const key = d.space_name || "Across your project";
      let space = project.spaces.find((s) => s.name === key);
      if (!space) {
        space = { name: key, items: [] };
        project.spaces.push(space);
      }
      space.items.push(d);
    });
    return out;
  }, [rows]);

  return (
    <div className="space-y-12" data-testid="decisions-page">
      <PageHeader
        overline="Your Decisions"
        title="Your Decisions"
        subtitle="Everything waiting for your review, all in one place."
        testId="decisions-header"
        actions={
          <div className="flex rounded-sm border border-line bg-white">
            {[
              ["open", "Needs Your Attention"],
              ["completed", "Completed"],
            ].map(([value, label]) => (
              <button
                key={value}
                data-testid={`decisions-filter-${value}`}
                onClick={() => setScope(value)}
                className={`px-4 py-2.5 text-xs uppercase tracking-[0.12em] transition-colors ${
                  scope === value ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      {rows === null ? (
        <div className="py-20 text-center text-sm text-fog">Loading your decisions…</div>
      ) : rows.length === 0 ? (
        <div className="card-surface flex flex-col items-start gap-3 p-10" data-testid="decisions-all-caught-up">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#EDEFC4]">
            <Check className="h-5 w-5 text-lime-dark" strokeWidth={1.5} />
          </span>
          <h3 className="font-serif text-3xl text-ink">
            {scope === "open" ? "You’re all caught up." : "Nothing completed yet."}
          </h3>
          <p className="text-sm text-stone">
            {scope === "open"
              ? "There are currently no decisions waiting for you."
              : "Decisions you’ve made will be kept here for your records."}
          </p>
        </div>
      ) : (
        grouped.map((project) => (
          <section key={project.project_id} data-testid={`decisions-project-${project.project_id}`}>
            <SectionTitle>{project.project_name}</SectionTitle>
            <div className="space-y-8">
              {project.spaces.map((space) => (
                <div key={space.name}>
                  <div className="overline mb-3">{space.name}</div>
                  <div className="card-surface">
                    {space.items.map((d) => (
                      <DecisionRow key={d.id} d={d} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
